import { ComponentPropsWithRef, ReactNode } from "react";
import styled, { css } from "styled-components";
import { staticText } from "../../shared/statics/static-text";
import {
  primary03,
  semanticRed01,
  shade0,
  shade700,
  shade850,
  shade900,
  shade950,
} from "../../styles/tokens";

// TEXT
export const text = staticText.selection;

// CONSTANTS
export const SELECTION_HEIGHTS: Record<Size, number> = {
  small: 2,
  medium: 2.5,
  large: 3,
};
export const SELECTION_PADDING_X = 1;
export const DISMISSIBLE_PADDING_RIGHT = 0.5;
export const DISMISS_GAP = 0.25;
export const DISMISS_AREA_SIZE = 1.5;

// STYLES
const containerFocusStyles = css`
  background-color: ${shade850};
`;

const centeredContentStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const DismissWrapper = styled.span`
  ${centeredContentStyles};

  width: ${DISMISS_AREA_SIZE}rem;
  height: ${DISMISS_AREA_SIZE}rem;
`;

export const IconWrapper = styled.span`
  ${centeredContentStyles};

  width: 1.5rem;
  height: 1.5rem;
`;

const dismissIconFocusStyles = css`
  > ${DismissWrapper} {
    color: ${primary03};
  }
`;

const wrapperStyles = css<{ $size?: Size }>`
  gap: 0.5rem;
  height: ${SELECTION_HEIGHTS.small}rem;
  display: flex;
  flex-shrink: 0;
  max-width: 100%;
  width: max-content;
  align-items: center;
  padding: 0.5rem ${SELECTION_PADDING_X}rem;
  background-color: ${shade900};

  ${({ $size }) =>
    $size === "medium" &&
    css`
      height: ${SELECTION_HEIGHTS.medium}rem;
    `}

  ${({ $size }) =>
    $size === "large" &&
    css`
      height: ${SELECTION_HEIGHTS.large}rem;
      padding: 0.75rem 1.5rem;
    `}
`;

export const Wrapper = styled.div<{ $size?: Size }>`
  ${wrapperStyles};
`;

export const Button = styled.button<{
  $size?: Size;
  $active?: boolean;
  $dismissible?: boolean;
  $invalid?: boolean;
  $dismissReady?: boolean;
  $variant?: Variant;
}>`
  ${wrapperStyles};

  ${({ $dismissible, $size }) =>
    $dismissible &&
    css`
      gap: ${DISMISS_GAP}rem;
      padding-right: ${$size === "large" ? 1 : DISMISSIBLE_PADDING_RIGHT}rem;
    `}

  cursor: pointer;

  &:hover:not([disabled]) {
    ${containerFocusStyles};

    ${({ $dismissReady }) =>
      $dismissReady &&
      css`
        ${dismissIconFocusStyles};
      `}
  }

  /* When dismissReady is false, only apply dismiss icon styles when hovering the icon itself */
  ${({ $dismissReady }) =>
    !$dismissReady &&
    css`
      &:not([disabled]) > ${DismissWrapper}:hover {
        color: ${primary03};
      }

      &:not([disabled]) > ${DismissWrapper}:focus-visible {
        color: ${primary03};
      }
    `}

  &:focus-visible:not([disabled]) {
    ${containerFocusStyles};

    ${({ $dismissReady }) =>
      $dismissReady &&
      css`
        ${dismissIconFocusStyles};
      `}

    box-shadow: inset 0 0 0 1px ${shade0};
  }

  ${({ $active, $dismissReady, $variant }) =>
    $active && $variant === "important"
      ? css`
          background-color: ${shade0};
          color: ${shade950};
          &:hover:not([disabled]),
          &:focus-visible:not([disabled]) {
            background-color: ${shade0};
            color: ${shade950};
          }
        `
      : $active && $dismissReady
        ? css`
            ${containerFocusStyles};
            ${dismissIconFocusStyles};
            box-shadow: inset 0 0 0 1px ${shade0};
          `
        : $active
          ? css`
              ${containerFocusStyles};
              box-shadow: inset 0 0 0 1px ${shade0};
            `
          : null}

  ${({ $invalid }) =>
    $invalid &&
    css`
      &,
      &:hover:not([disabled]),
      &:focus-visible:not([disabled]) {
        box-shadow: inset 0 0 0 1px ${semanticRed01};
      }
    `}

  &[disabled] {
    cursor: not-allowed;
    color: ${shade700};
  }
`;

// DATA

// TYPES
/** `small` is 32px tall, `medium` (the default) is 40px and `large` is 48px. */
export type Size = "small" | "medium" | "large";

type Variant = "default" | "important";

export interface Props extends ComponentPropsWithRef<"button"> {
  value: string;
  /** Shown before the value. Inherits the text color. */
  icon?: ReactNode;
  $size?: Size;
  $variant?: Variant;
  /** Marks the value as wrong with a red outline. */
  invalid?: boolean;
  active?: boolean;
  passive?: boolean;
  focusable?: boolean;
  unclearable?: boolean;
  /**
   * When true, the dismiss icon will highlight on entire component hover/focus
   * When false, the dismiss icon will only highlight when hovering/focusing over the icon itself
   */
  dismissReady?: boolean;
  /**
   * Called when the selection is dismissed:
   * - If dismissReady is true: clicking anywhere on the component calls onDismiss
   * - If dismissReady is false: only clicking the dismiss icon itself calls onDismiss
   */
  onDismiss?: () => void;
}

export type SkeletonProps = {
  value?: string;
  $size?: Size;
  $variant?: Variant;
};

// FUNCTIONS

// ASSETS
